import { useTranslation } from 'react-i18next';
import { Modal } from '@/ui/Modal/Modal';
import { Button } from '@/ui/Button/Button';
import { useTheme } from '@/theme/ThemeProvider';
import {
  A11Y_PREFERENCES,
  A11Y_PROFILES,
  useA11yPreferences,
} from './A11yPreferencesProvider';
import type { A11yPreference } from './A11yPreferencesProvider';
import styles from './A11yPanel.module.css';

/**
 * Etichette delle preferenze. Le chiavi `a11y_*` esistono già tradotte in 11
 * lingue nel dizionario migrato: si riusano, non si reinventano.
 * Dove la chiave non esiste il fallback italiano è dichiarato e va aggiunto
 * al dizionario (vedi docs/i18n-migrazione.md, "Chiavi da creare").
 */
const PREFERENCE_LABELS: Record<A11yPreference, { key: string; fallback: string }> = {
  contrast: { key: 'a11y_high_contrast', fallback: 'Alto contrasto' },
  'large-text': { key: 'a11y_large_text', fallback: 'Testo grande' },
  'larger-text': { key: 'a11y_larger_text', fallback: 'Testo molto grande' },
  dyslexic: { key: 'a11y_dyslexia', fallback: 'Font per dislessia' },
  'text-spacing': { key: 'a11y_text_spacing', fallback: 'Spaziatura del testo' },
  'underline-links': { key: 'a11y_underline_links', fallback: 'Sottolinea i link' },
  'reduce-motion': { key: 'a11y_reduce_motion', fallback: 'Riduci animazioni' },
};

/**
 * Pannello delle preferenze di accessibilità.
 *
 * È dichiarato esplicitamente come **preferenze utente, non overlay**: i
 * toggle mutano i token CSS del documento nativo. Nessuna riscrittura del DOM,
 * nessun ruolo ARIA aggiunto a runtime — che è esattamente ciò che rende gli
 * overlay di accessibilità dannosi.
 *
 * Ogni opzione è un `<button aria-pressed>`, non una checkbox: sono azioni che
 * cambiano immediatamente la resa, non valori da inviare con un form.
 * Lo stato è comunicato tre volte: `aria-pressed`, il testo "Attivo"/"Non
 * attivo", e il bordo. Non solo dal colore.
 */
export function A11yPanel() {
  const { t } = useTranslation();
  const { active, isActive, toggle, applyProfile, reset, panelOpen, setPanelOpen } =
    useA11yPreferences();
  const { theme, toggleTheme } = useTheme();

  const stateOn = t('a11y_state_on', 'Attivo');
  const stateOff = t('a11y_state_off', 'Non attivo');

  return (
    <Modal
      open={panelOpen}
      onClose={() => setPanelOpen(false)}
      title={t('a11y_panel_title', 'Strumenti di accessibilità')}
      description={t(
        'a11y_panel_description',
        'Queste sono preferenze personali: modificano la presentazione della pagina e restano salvate su questo dispositivo. Non è un livello sovrapposto al sito.',
      )}
      closeLabel={t('a11y_panel_close', 'Chiudi')}
      footer={
        <>
          <Button variant="secondary" onClick={reset} disabled={active.length === 0}>
            {t('a11y_btn_reset_all', 'Disattiva tutto')}
          </Button>
          <Button variant="primary" onClick={() => setPanelOpen(false)}>
            {t('a11y_panel_close', 'Chiudi')}
          </Button>
        </>
      }
    >
      <section className={styles.section} aria-labelledby="a11y-profiles-title">
        <h3 id="a11y-profiles-title" className={styles.sectionTitle}>
          {t('a11y_sec_nav', 'Profili rapidi')}
        </h3>
        <p className={styles.note}>
          {t(
            'a11y_profiles_note',
            'Un profilo accende più preferenze insieme. Puoi poi modificarle una per una.',
          )}
        </p>
        <ul className={styles.profiles}>
          {A11Y_PROFILES.map((profile) => (
            <li key={profile.id}>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => applyProfile(profile.id)}
              >
                {t(profile.labelKey, profile.id)}
              </Button>
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.section} aria-labelledby="a11y-prefs-title">
        <h3 id="a11y-prefs-title" className={styles.sectionTitle}>
          {t('a11y_sec_viz', 'Preferenze')}
        </h3>
        <ul className={styles.options}>
          {A11Y_PREFERENCES.map((preference) => {
            const label = PREFERENCE_LABELS[preference];
            const on = isActive(preference);
            return (
              <li key={preference}>
                <button
                  type="button"
                  className={styles.option}
                  aria-pressed={on}
                  onClick={() => toggle(preference)}
                >
                  <span>{t(label.key, label.fallback)}</span>
                  {/* Lo stato in chiaro: chi non distingue i colori e chi non
                      usa uno screen reader deve poterlo leggere. */}
                  <span className={styles.state}>{on ? stateOn : stateOff}</span>
                </button>
              </li>
            );
          })}
          {/* Il tema scuro sta QUI dal 29/07/2026: l'header allineato al
              riferimento non ha il toggle in barra, ma la scelta resta
              raggiungibile (e il default segue prefers-color-scheme). */}
          <li>
            <button
              type="button"
              className={styles.option}
              aria-pressed={theme === 'dark'}
              onClick={toggleTheme}
            >
              <span>{t('theme_toggle_dark', 'Tema scuro')}</span>
              <span className={styles.state}>
                {theme === 'dark' ? stateOn : stateOff}
              </span>
            </button>
          </li>
        </ul>
      </section>
    </Modal>
  );
}
